import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Share, PlusSquare } from "lucide-react";
import { canInstall, triggerInstall, isStandalone, isIOSSafari } from "@/lib/pwa";

const DISMISS_KEY = "sg-install-dismissed";

// Leaf SVG icon (matches app icon / login screen)
function LeafIcon({ size = 40 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
      <rect width="100" height="100" rx="22" fill="#E9F0E8" />
      <path
        d="M50 78 L50 35 Q50 22 38 22 Q26 22 26 35 Q26 48 50 50 Q74 48 74 35 Q74 22 62 22 Q50 22 50 35"
        fill="#4A6B53"
      />
    </svg>
  );
}

export default function InstallPrompt() {
  const [show, setShow] = useState(false);
  const ios = isIOSSafari();

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY)) return;

    if (ios) {
      const t = setTimeout(() => setShow(true), 3000);
      return () => clearTimeout(t);
    }

    // Listen for pwa-installable event fired by captureDeferredPrompt()
    const handler = () => setTimeout(() => setShow(true), 2000);
    window.addEventListener("pwa-installable", handler);
    // Also show if already capturable when component mounts
    if (canInstall()) setTimeout(() => setShow(true), 2000);
    return () => window.removeEventListener("pwa-installable", handler);
  }, [ios]);

  const dismiss = () => {
    setShow(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    const ok = await triggerInstall();
    if (ok) setShow(false);
  };

  return (
    <AnimatePresence>
      {show && (
        <>
          {/* Scrim */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/30 z-40"
            onClick={dismiss}
          />

          {/* Bottom sheet */}
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 32 }}
            className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl px-6 pt-5 pb-10 shadow-2xl max-w-md mx-auto"
          >
            {/* Drag handle */}
            <div className="w-10 h-1 bg-[#E9EFEA] rounded-full mx-auto mb-5" />

            {/* Close */}
            <button
              onClick={dismiss}
              className="absolute top-5 right-5 text-[#6B7A70]"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-4 mb-5">
              <LeafIcon size={52} />
              <div>
                <div className="font-heading text-lg font-semibold text-[#2A332C]">
                  Skin Garden
                </div>
                <div className="text-sm text-[#6B7A70]">Add to your home screen</div>
              </div>
            </div>

            {ios ? (
              /* iOS instructions */
              <div className="space-y-3">
                <p className="text-sm text-[#2A332C]">
                  Install this app for the full experience — offline access,
                  push reminders, and a native feel.
                </p>
                <div className="flex items-start gap-3 p-3 rounded-2xl bg-[#F7F5F0]">
                  <span className="text-xl mt-0.5">1</span>
                  <div className="flex items-center gap-2 text-sm text-[#2A332C]">
                    Tap{" "}
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-[#E9EFEA] rounded-lg font-medium">
                      <Share className="w-3.5 h-3.5" /> Share
                    </span>{" "}
                    in Safari&rsquo;s toolbar
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-2xl bg-[#F7F5F0]">
                  <span className="text-xl mt-0.5">2</span>
                  <div className="flex items-center gap-2 text-sm text-[#2A332C]">
                    Tap{" "}
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-[#E9EFEA] rounded-lg font-medium">
                      <PlusSquare className="w-3.5 h-3.5" /> Add to Home Screen
                    </span>
                  </div>
                </div>
                <button
                  onClick={dismiss}
                  className="w-full mt-2 py-3 rounded-full bg-[#2A332C] text-white font-medium text-sm"
                >
                  Got it
                </button>
              </div>
            ) : (
              /* Android / desktop install button */
              <div className="space-y-3">
                <p className="text-sm text-[#2A332C]">
                  Install for offline access, push reminders, and a faster
                  native experience.
                </p>
                <button
                  onClick={install}
                  className="w-full py-3 rounded-full bg-[#2A332C] text-white font-medium text-sm"
                >
                  Install app
                </button>
                <button
                  onClick={dismiss}
                  className="w-full py-2 rounded-full text-[#6B7A70] text-sm"
                >
                  Not now
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
